import React, { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'

import { getInvoice, getRecordStatuses } from '../../api'
import { Loading } from '../common'
import { Invoice } from '../../models'
import { getAuthUser } from '../../helpers'
import defineAbilityFor from './defineAbility'
import { InsufficientPermissions } from '../common'
import { currencyFormat } from '../../helpers/currencyCalculation'
import { CreateInvoiceForm } from './createInvoiceForm'
import { RecordStatus, JsonRecordStatus } from '../../models'
import { DownloadInvoice } from './download'

enum PageState {
    Default,
    Loading,
    Found,
    NotFound,
    AuthenticationError,
    Error,
}

export const InvoiceComponent = () => {
    const { invoiceId } = useParams()
    const navigate = useNavigate()

    const [invoice, setInvoice] = useState<Invoice | undefined>()
    const [pageState, setPageState] = useState(PageState.Default)

    // indicates that the user is selecting records to include in a regenerated invoice
    const [isCloningInvoice, setIsCloningInvoice] = useState(false)
    const [selectedRecords, setSelectedRecords] = useState<{ [id: string]: boolean }>({})
    // indicates if the createInvoice request is in progress
    const [isCloneSubmitting, setIsCloneSubmitting] = useState(false)

    const user = getAuthUser()

    const ability = defineAbilityFor(user)

    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
    const [sortField, setSortField] = useState<'statusName' | 'startDate'>('statusName')

    const sortedRecords = React.useMemo(() => {
        if (!invoice) return []
        return [...invoice.records].sort((a, b) => {
            let aValue: string | number = ''
            let bValue: string | number = ''

            if (sortField === 'statusName') {
                aValue = a.statusName
                bValue = b.statusName
            } else if (sortField === 'startDate') {
                aValue = a.startDate.valueOf()
                bValue = b.startDate.valueOf()
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
            return 0
        })
    }, [invoice?.records, sortField, sortDirection])

    const toggleSort = (field: 'statusName' | 'startDate') => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const [recordStatuses, setRecordStatuses] = useState<RecordStatus[]>([])
    const [selectedStatus, setSelectedStatus] = useState<number | ''>('')

    useEffect(() => {
        let canceled = false

        setIsCloningInvoice(false)
        setSelectedRecords({})

        if (invoiceId && ability.can('View', 'Invoice')) {
            setPageState(PageState.Loading)

            getInvoice(invoiceId)
                .then((response) => {
                    if (canceled) return
                    setPageState(PageState.Found)
                    setInvoice(new Invoice(response.data.invoice, response.data.records))
                })
                .catch((error) => {
                    if (canceled) return
                    setPageState(PageState.Error)

                    const { response } = error

                    if (response?.status === 404) {
                        setPageState(PageState.NotFound)
                    }
                    if (response?.status === 401) {
                        setPageState(PageState.AuthenticationError)
                    }
                })

            getRecordStatuses().then((json) => {
                if (canceled) return
                const statuses = json.data.map(
                    (status: JsonRecordStatus) => new RecordStatus(status),
                )
                setRecordStatuses(statuses)
            })
        }
        return () => {
            canceled = true
        }
    }, [invoiceId])

    if (!ability.can('View', 'Invoice')) {
        return <InsufficientPermissions />
    }

    const handleRecordSelected = (id: number | undefined, selected: boolean) => {
        if (!invoice) {
            return
        }

        if (id == undefined) {
            if (selected) {
                setSelectedRecords(
                    invoice.records.reduce(
                        (obj, r) => {
                            obj[r.id] = true
                            return obj
                        },
                        {} as { [id: string]: boolean },
                    ),
                )
                setSelectedStatus('')
            } else {
                setSelectedRecords({})
            }

            return
        }

        const newSelectedRecords = {
            ...selectedRecords,
        }

        if (selected) {
            newSelectedRecords[id] = true
        } else {
            delete newSelectedRecords[id]
        }

        setSelectedRecords(newSelectedRecords)
    }

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value
        const statusCode = Number(value)

        if (!invoice || value === '') return

        setSelectedStatus(statusCode)

        const matchingRecords = invoice.records.filter(
            (record) => record.statusCode === statusCode,
        )

        const newSelectedRecords: { [id: string]: boolean } = {}
        matchingRecords.forEach((record) => {
            newSelectedRecords[record.id] = true
        })

        setSelectedRecords(newSelectedRecords)
    }

    switch (pageState) {
        case PageState.NotFound:
            return (
                <div className="container">
                    <h3>Invoice Not Found</h3>
                </div>
            )
        case PageState.Found:
            if (invoice instanceof Invoice) {
                return (
                    <div className="container">
                        <div className="row">
                            <div className="d-flex col">
                                <div className="d-flex align-items-center">
                                    <h3 className="d-inline-block mb-2 me-4">
                                        Invoice #{invoice.id}
                                        <DownloadInvoice
                                            invoice={invoice} />
                                    </h3>
                                    <Link
                                        to={`/invoices/reconciliation/${invoice.id}`}
                                        className="btn btn-primary me-3"
                                    >
                                        Reconcile
                                    </Link>
                                    <div>
                                        <button
                                            className={`btn me-1 ${(isCloningInvoice && 'btn-danger') || 'btn-primary'}`}
                                            disabled={isCloneSubmitting}
                                            type="button"
                                            onClick={(e) =>
                                                setIsCloningInvoice(!isCloningInvoice)
                                            }
                                        >
                                            {(isCloningInvoice && 'Cancel') || 'Clone Invoice'}
                                        </button>
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div
                                        className="d-flex align-items-end"
                                        style={{
                                            visibility: isCloningInvoice
                                                ? 'visible'
                                                : 'hidden',
                                        }}
                                    >
                                        <div className="px-3 py-2">
                                            Records selected:{' '}
                                            {Object.keys(selectedRecords).length}
                                        </div>
                                        <CreateInvoiceForm
                                            supplier={invoice.supplier}
                                            userId={user.id}
                                            records={Object.keys(selectedRecords).map((id) =>
                                                parseInt(id),
                                            )}
                                            disableSubmit={
                                                Object.keys(selectedRecords).length === 0
                                                    ? 'Select at least one record to include in the new invoice'
                                                    : undefined
                                            }
                                            onSubmit={() => setIsCloneSubmitting(true)}
                                            onSuccess={(response) => {
                                                setIsCloneSubmitting(false)
                                                navigate(
                                                    `/invoices/${response.data.invoice.id}`,
                                                )
                                            }}
                                            onError={() => setIsCloneSubmitting(false)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            className="d-flex align-items-center mb-3"
                            style={{ visibility: isCloningInvoice ? 'visible' : 'hidden' }}
                        >
                            <label htmlFor="statusInput" className="me-2">
                                Status
                            </label>
                            <select
                                className="form-select w-auto"
                                id="statusInput"
                                value={selectedStatus}
                                onChange={handleStatusChange}
                            >
                                <option value="">Select status</option>
                                {recordStatuses.map((status) => (
                                    <option key={status.code} value={status.code}>
                                        {status.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {isCloningInvoice && (
                            <div className="row">
                                <div className="col d-flex justify-content-end">
                                    <div></div>
                                </div>
                            </div>
                        )}
                        <div className="row">
                            <div className="col">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            {isCloningInvoice && (
                                                <th>
                                                    <input
                                                        className="form-check-input"
                                                        onChange={(e) =>
                                                            handleRecordSelected(
                                                                undefined,
                                                                e.target.checked,
                                                            )
                                                        }
                                                        type="checkbox"
                                                        checked={
                                                            Object.keys(selectedRecords)
                                                                .length > 0
                                                        }
                                                    />
                                                </th>
                                            )}
                                            <th>Record</th>
                                            <th>Agency</th>
                                            <th>ARC</th>
                                            <th>Confirmation Number</th>
                                            <th>Name</th>
                                            <th>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => toggleSort('startDate')}
                                                >
                                                    Check In
                                                    <span>
                                                        {sortField === 'startDate'
                                                            ? sortDirection === 'asc'
                                                                ? '▲'
                                                                : '▼'
                                                            : ''}
                                                    </span>
                                                </div>
                                            </th>
                                            <th>Nights</th>
                                            <th>Nightly Rate</th>
                                            <th>Commission Due</th>
                                            <th>Paid CT</th>
                                            <th>Payment Method</th>
                                            <th
                                                onClick={() => toggleSort('statusName')}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    Status
                                                    <span>
                                                        {sortField === 'statusName'
                                                            ? sortDirection === 'asc'
                                                                ? '▲'
                                                                : '▼'
                                                            : ''}
                                                    </span>
                                                </div>
                                            </th>
                                            <th>Substatus</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedRecords.map((record) => (
                                            <tr key={record.id}>
                                                {isCloningInvoice && (
                                                    <td>
                                                        <input
                                                            checked={
                                                                selectedRecords[record.id] ===
                                                                true
                                                            }
                                                            className="form-check-input"
                                                            onChange={(e) =>
                                                                handleRecordSelected(
                                                                    record.id,
                                                                    e.target.checked,
                                                                )
                                                            }
                                                            disabled={isCloneSubmitting}
                                                            type="checkbox"
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <Link to={`/records/${record.id}`}>
                                                        {record.id}
                                                    </Link>
                                                </td>
                                                <td>{record.agencyName}</td>
                                                <td>{record.arc}</td>
                                                <td>{record.confirmationNumber}</td>
                                                <td>
                                                    {record.guestLastName},{' '}
                                                    {record.guestFirstName}
                                                </td>
                                                <td>
                                                    {record.startDate.format('YYYY-MM-DD')}
                                                </td>
                                                <td>{record.days}</td>
                                                <td>{currencyFormat(record.rate)}</td>
                                                <td>{currencyFormat(record.commission)}</td>
                                                <td>{currencyFormat(record.paidCt)}</td>
                                                <td>{record.paymentMethodName ?? 'N/A'}</td>
                                                <td>{record.statusName}</td>
                                                <td>{record.substatusName}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <h4 className="mb-5 d-flex justify-content-end align-items-end">
                                    Total records: {invoice.records.length}
                                </h4>
                            </div>
                        </div>
                    </div>
                )
            }
        case PageState.AuthenticationError:
            return (
                <div className="container">
                    <h3 className="text-center">Session expired, please refresh.</h3>
                </div>
            )
        case PageState.Default:
        case PageState.Loading:
        default:
            return (
                <div className="container">
                    <Loading />
                </div>
            )
    }
}
